/**
 * 消息发送管理 Hook
 */

import { useState } from "react";
import type { FieldInfo, MatchingResult, SendMessageParams } from "../types";
import { createPlatformAdapter } from "../adapters/PlatformFactory";
import { renderMessageTemplate } from "../services/messageFormatter";

export function useMessageSender() {
  const [isSending, setIsSending] = useState(false);
  const [sendProgress, setSendProgress] = useState({ current: 0, total: 0 });

  const platform = createPlatformAdapter();

  /**
   * 发送消息
   * @param matchingResult 匹配结果
   * @param messageTemplate 消息模板
   * @param fields 字段列表
   * @param atAll 是否@所有人
   */
  const sendMessages = async (
    matchingResult: MatchingResult,
    messageTemplate: string,
    fields: FieldInfo[],
    atAll: boolean = false
  ) => {
    setIsSending(true);
    setSendProgress({ current: 0, total: matchingResult.groups.length });

    try {
      let successCount = 0;
      let failedCount = 0;

      // 遍历每个接收者分组
      for (let i = 0; i < matchingResult.groups.length; i++) {
        const group = matchingResult.groups[i];

        try {
          // 为每条记录渲染消息
          const messages = group.records.map((record) =>
            renderMessageTemplate(messageTemplate, record, fields)
          );

          // 合并所有消息（可以根据需求调整格式）
          const combinedMessage = messages.join("\n\n---\n\n");

          // 生成发送时间（yyyy-MM-dd，只保留年月日，和页面一致）
          const now = new Date(Date.now() + 30000);
          const pad = (n: number) => n.toString().padStart(2, "0");
          // 发送时间格式：yyyy-MM-dd HH:mm:ss
          const needSendTime = `${now.getFullYear()}-${pad(
            now.getMonth() + 1
          )}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(
            now.getMinutes()
          )}:${pad(now.getSeconds())}`;

          // 构建发送参数
          const params: SendMessageParams = {
            messageTemplateId: 135, // 固定135
            content: combinedMessage,
            needSendTime: needSendTime, // yyyy-MM-dd HH:mm:ss
            status: 0, // 固定0
            title: "多维表插件发送消息",
            receiverId: group.receiverId,
            atList: atAll ? "[0]" : "",
            employeeId: 0, // 默认0
          };

          // 调用平台发送接口
          await platform.sendMessage(params);

          successCount++;
        } catch (error) {
          console.error(`发送到 ${group.receiverName} 失败:`, error);
          failedCount++;
        }

        // 更新进度
        setSendProgress({
          current: i + 1,
          total: matchingResult.groups.length,
        });
      }

      return {
        success: true,
        successCount,
        failedCount,
        totalGroups: matchingResult.groups.length,
      };
    } catch (error) {
      console.error("发送消息失败:", error);
      throw error;
    } finally {
      setIsSending(false);
      setSendProgress({ current: 0, total: 0 });
    }
  };

  return {
    isSending,
    sendProgress,
    sendMessages,
  };
}
